/**
 * Scalar primitives of the CLM API contract.
 *
 * Conventions enforced across this package:
 *
 * 1. **Wire field names are `snake_case`** — CLAUDE.md rule 12. The T-SQL
 *    PascalCase in `docs/wp-01-schema-tsql-superseded.md` does not carry forward.
 * 2. **No `.default()` anywhere in this package.** A default makes a Zod schema's
 *    input type differ from its output type, which would give the front end and
 *    the gateway two different views of the same contract. Server-side defaults
 *    are exported as plain constants instead (see `pagination.ts`).
 * 3. **`.nullable()` is preferred over `.optional()`** for response fields, so a
 *    fact that is absent is explicit on the wire rather than missing.
 * 4. **Surrogate database keys are opaque strings**, never numbers. A number
 *    would let a client depend on it being a Postgres `bigint`, which is exactly
 *    the kind of internal shape this package exists to keep out.
 */
import { z } from 'zod';
/* -------------------------------------------------------------------------- */
/* Time                                                                        */
/* -------------------------------------------------------------------------- */
/** ISO 8601 calendar date with no time component — `2026-08-05`. */
export const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const IsoDateSchema = z
    .string()
    .regex(ISO_DATE_PATTERN, 'expected an ISO 8601 calendar date (YYYY-MM-DD)');
/**
 * ISO 8601 instant, **UTC only** — `2026-08-05T07:00:00Z`.
 *
 * Every timestamp in this contract is named `*_utc` and must carry the trailing
 * `Z`. Local offsets are rejected: the notification ledger is keyed on
 * `occurrence_date`, and an offset-bearing timestamp would let the same
 * occurrence resolve to two dates.
 */
export const ISO_TIMESTAMP_UTC_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?Z$/;
export const IsoTimestampUtcSchema = z
    .string()
    .regex(ISO_TIMESTAMP_UTC_PATTERN, 'expected an ISO 8601 UTC instant ending in Z');
/* -------------------------------------------------------------------------- */
/* Actors                                                                      */
/* -------------------------------------------------------------------------- */
/**
 * Entra user principal name. Present in responses as the recorded actor; it is
 * **never** accepted as a request field — the actor is derived from the bearer
 * token, never asserted by the caller.
 */
export const UpnSchema = z
    .string()
    .min(3)
    .max(120)
    .regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, 'expected a user principal name');
/* -------------------------------------------------------------------------- */
/* Opaque identifiers                                                          */
/* -------------------------------------------------------------------------- */
/**
 * An identifier minted by the server whose internal representation is not part
 * of the contract. Clients round-trip it and must not parse, order or arithmetic
 * on it.
 */
export const OpaqueIdSchema = z.string().min(1).max(64);
/** Correlation identifier threaded through audit rows (NFR-07). */
export const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
export const CorrelationIdSchema = z.string().regex(UUID_PATTERN, 'expected a lower-case UUID');
/** The largest `bigint` (`int8`), 2^63 − 1, as the decimal text `pg` returns it. */
const INT8_MAX_TEXT = '9223372036854775807';
/**
 * A `bigint` identity carried as its decimal text — `pg` returns `int8` as a
 * string, and a JavaScript number would silently lose precision above 2^53.
 *
 * Bounded by the **column**, not only by its 19 digits: `9999999999999999999`
 * has the digit count but not the range, and a handler's `$1::bigint` cast
 * would raise 22003 — a `500` for what is the caller's own malformed input,
 * and so a `400` here, before any statement (the `manage-clause.ts`
 * feedback-id guard is the precedent, for 22P02). Compared as text, because
 * equal-length digit strings order as numbers, so a value that failed the
 * pattern never reaches `BigInt()`.
 */
export function bigintIdTextSchema(message) {
    return z
        .string()
        .regex(/^[1-9][0-9]{0,18}$/, message)
        .refine((value) => value.length < INT8_MAX_TEXT.length || value <= INT8_MAX_TEXT, message);
}
/* -------------------------------------------------------------------------- */
/* Party key — TD-02                                                           */
/* -------------------------------------------------------------------------- */
/**
 * Party key. TD-02 fixes one namespace with a type prefix:
 *
 * | Party type | Form        | Example |
 * |------------|-------------|---------|
 * | `Client`   | 3 letters   | `SGW`   |
 * | `Partner`  | `P` + 3     | `PACM`  |
 * | `Vendor`   | `V` + 3     | `VMSF`  |
 *
 * Clients stay unprefixed so an existing Qfly Client Key drops in unchanged,
 * which is why a client key may not begin with `P` or `V`.
 */
export const CLIENT_PARTY_KEY_PATTERN = /^[A-OQ-UW-Z][A-Z]{2}$/;
export const PARTNER_PARTY_KEY_PATTERN = /^P[A-Z]{3}$/;
export const VENDOR_PARTY_KEY_PATTERN = /^V[A-Z]{3}$/;
export const PARTY_KEY_PATTERN = /^(?:[A-OQ-UW-Z][A-Z]{2}|[PV][A-Z]{3})$/;
export const ClientPartyKeySchema = z
    .string()
    .regex(CLIENT_PARTY_KEY_PATTERN, 'a Client party key is 3 upper-case letters and may not start with P or V');
export const PartnerPartyKeySchema = z
    .string()
    .regex(PARTNER_PARTY_KEY_PATTERN, 'a Partner party key is P followed by 3 upper-case letters');
export const VendorPartyKeySchema = z
    .string()
    .regex(VENDOR_PARTY_KEY_PATTERN, 'a Vendor party key is V followed by 3 upper-case letters');
export const PartyKeySchema = z.string().regex(PARTY_KEY_PATTERN, 'expected a TD-02 party key');
/* -------------------------------------------------------------------------- */
/* Contract key — DR-01 / TD-02                                                */
/* -------------------------------------------------------------------------- */
/**
 * Contract key — DR-01 as amended by **D-110-1** (1 Oct 2026, CLM-110). Two
 * shapes, and only two:
 *
 *  - **party** — `PartyKey-nnnn`, e.g. `SGW-0012`: the primary party and a
 *    four-digit number per party, with **no type**. Every key minted from
 *    1 Oct 2026 on has this shape ({@link PARTY_CONTRACT_KEY_PATTERN}).
 *  - **typed** — `PartyKey-Type-Number`, e.g. `SGW-MSA-0003`, issued before
 *    then. Never minted again, and **never changed**: keys are permanent
 *    ({@link TYPED_CONTRACT_KEY_PATTERN}).
 *
 * The two are disjoint — after `PartyKey-`, a typed key has a letter and a party
 * key has a digit — so no string matches both, and no new key can equal an old
 * one. It is `PartyKey-`, **not** `ClientKey-`: the primary party is not always
 * a Client (TD-02). A typed key's middle segment stops being the contract's type
 * once an Admin corrects it (`contract_type` is the type), and nothing reads it
 * for a rule (DR-02).
 *
 * The key is *never parsed* to recover lineage — DR-02 requires the three
 * lineage relationships to be explicit columns. The `-A<n>` suffix on the typed
 * shape is matched only so keys issued with it validate; it carries no
 * authority, and the party shape has none (an amendment gets its own number and
 * follows its agreement through lineage, R-110-11).
 *
 * The wire keeps its pre-B-14 `\d{3,4}` tolerance for the **typed** shape only —
 * many fixtures use three digits, and the database (`ck_contract_key_format`) is
 * the narrower of the two, which is the safe direction. The party shape is
 * exactly `\d{4}` here and there.
 */
export const TYPED_CONTRACT_KEY_PATTERN = /^(?:[A-OQ-UW-Z][A-Z]{2}|[PV][A-Z]{3})-[A-Z]{3,12}-\d{3,4}(?:-A\d{1,2})?$/;
export const PARTY_CONTRACT_KEY_PATTERN = /^(?:[A-OQ-UW-Z][A-Z]{2}|[PV][A-Z]{3})-\d{4}$/;
/**
 * The union of {@link TYPED_CONTRACT_KEY_PATTERN} and
 * {@link PARTY_CONTRACT_KEY_PATTERN}, written as one anchored regex so neither
 * alternative escapes an anchor. A test pins that it accepts exactly what one of
 * the two named patterns accepts.
 */
export const CONTRACT_KEY_PATTERN = /^(?:[A-OQ-UW-Z][A-Z]{2}|[PV][A-Z]{3})-(?:[A-Z]{3,12}-\d{3,4}(?:-A\d{1,2})?|\d{4})$/;
export const ContractKeySchema = z
    .string()
    .max(32)
    .regex(CONTRACT_KEY_PATTERN, 'expected a contract key of the form PartyKey-nnnn, or PartyKey-Type-nnnn issued before 1 Oct 2026');
/**
 * Groups an entire lineage (§8.1) — the value you query to answer *"show me
 * this agreement's whole history"*.
 */
export const AgreementFamilyIdSchema = z.string().min(1).max(32);
/* -------------------------------------------------------------------------- */
/* Clause identity                                                             */
/* -------------------------------------------------------------------------- */
/**
 * Natural key of a library clause — `LIAB-CAP-STD`. Preferred over the surrogate
 * `clause_id` everywhere a clause is *addressed*, so the contract does not
 * depend on a database identity column.
 */
export const ClauseCodeSchema = z
    .string()
    .min(2)
    .max(32)
    .regex(/^[A-Z0-9][A-Z0-9-]*$/, 'expected an upper-case clause code');
/** A 1-based version number (clause library FR-17, contract lineage §8.1). */
export const VersionNoSchema = z.number().int().min(1);
/* -------------------------------------------------------------------------- */
/* Document spans                                                              */
/* -------------------------------------------------------------------------- */
/**
 * A half-open character range into the converted document text.
 *
 * Clause segmentation returns **spans, not text** — CLAUDE.md rule 13. Echoing
 * clause text through the API would duplicate the one copy that lives in the
 * authoritative index, and asking a model to reproduce it costs ~5× input and
 * introduces transcription errors.
 */
export const TextSpanSchema = z.object({
    start_offset: z.number().int().min(0),
    end_offset: z.number().int().min(0),
});
/** A normalised score in `[0, 1]`. */
export const UnitScoreSchema = z.number().min(0).max(1);
/** SHA-256 hex digest — used to evidence which bytes were uploaded. */
export const Sha256Schema = z.string().regex(/^[a-f0-9]{64}$/, 'expected a lower-case SHA-256 hex digest');
/**
 * An `https://` URL to a resource a user may open — a SharePoint item, a
 * DocuSign completion certificate. Document **bytes** are never carried by this
 * contract; the front end follows the link.
 */
export const HttpsUrlSchema = z
    .string()
    .min(9)
    .max(2000)
    .regex(/^https:\/\/[^\s]+$/, 'expected an https URL');
//# sourceMappingURL=primitives.js.map